import { useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, MapPin } from "lucide-react";
import { DeckHero, STAGES } from "./DeckHero";
import { Lines, EASE } from "./Motion";
import { scrollToId } from "@/lib/lenis";

const fadeUp = (ready, delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: ready ? { opacity: 1, y: 0 } : {},
  transition: { delay, duration: 1, ease: EASE },
});

export const Hero = ({ ready }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const sceneY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const wordX = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const [stage, setStage] = useState(0);
  const done = stage === STAGES.length - 1;

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-[#0A0A0A]">
      <motion.div style={{ y: sceneY }} className="absolute inset-0 lg:left-[28%]">
        {ready && <DeckHero progress={scrollYProgress} onStage={setStage} testId="hero-3d-canvas" className="h-full w-full" />}
      </motion.div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/75 to-transparent lg:via-[#0A0A0A]/25" />
      <div className="pointer-events-none absolute inset-0 bg-[#0A0A0A]/45 lg:hidden" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-[#0A0A0A] to-transparent" />

      <motion.div style={{ x: wordX }} className="pointer-events-none absolute -bottom-[3vw] left-0 select-none whitespace-nowrap font-display text-[26vw] font-extrabold leading-none tracking-tighter text-outline">
        Newland Newland
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1500px] flex-col justify-center px-5 pb-28 pt-32 md:px-10">
        <motion.div {...fadeUp(ready, 0.2)} className="eyebrow mb-8 flex items-center gap-3 text-neutral-300">
          <MapPin className="h-3.5 w-3.5" /> Serving the Tri-Cities region
        </motion.div>
        <h1 data-testid="hero-title" className="max-w-3xl font-display text-[2.8rem] font-extrabold leading-[0.95] tracking-tighter text-neutral-50 sm:text-6xl lg:text-7xl xl:text-[5.6rem]">
          <Lines trigger={ready} delay={0.25} lines={[
            "Decks. Porches.",
            "Siding.",
            <span key="l3" className="font-serif font-normal italic tracking-tight text-neutral-300">Built to outlast.</span>,
          ]} />
        </h1>
        <motion.p {...fadeUp(ready, 0.8)} className="mt-8 max-w-lg text-base leading-relaxed text-neutral-400 md:text-lg">
          Owner-operated by Matt Newland since 2022. New construction decks, covered porches, siding and exterior upgrades for homeowners across Johnson City, Kingsport and Bristol.
        </motion.p>
        <motion.div {...fadeUp(ready, 1)} className="mt-10 flex flex-wrap items-center gap-4">
          <button data-testid="hero-get-quote-button" onClick={() => scrollToId("quote")}
            className="group flex items-center gap-3 rounded-full bg-white py-4 pl-7 pr-5 font-semibold text-[#0A0A0A] transition-colors hover:bg-neutral-200">
            Get a free estimate
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0A0A0A] text-white transition-transform duration-500 group-hover:rotate-45">
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </button>
          <button data-testid="hero-explore-button" onClick={() => scrollToId("transformations")}
            className="rounded-full border border-white/15 px-7 py-4 font-medium text-neutral-100 backdrop-blur-sm transition-colors hover:border-white/60">
            Watch the transformations
          </button>
        </motion.div>
      </motion.div>

      <motion.div {...fadeUp(ready, 1.3)} className="absolute inset-x-0 bottom-0 z-10 mx-auto flex max-w-[1500px] items-end justify-between pb-8 pl-5 pr-20 md:px-10">
        <div data-testid="hero-3d-hint" className="flex items-center gap-3 rounded-full border border-white/10 bg-[#0A0A0A]/60 px-4 py-2 backdrop-blur-md">
          <span className="relative flex h-2 w-2">
            {!done && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-60" />}
            <span className={`relative inline-flex h-2 w-2 rounded-full ${done ? "bg-[#FFE6BF]" : "bg-white"}`} />
          </span>
          <span className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500">{done ? "Built" : `Step ${stage + 1}/${STAGES.length - 1}`}</span>
          <AnimatePresence initial={false}>
            <motion.span key={stage} data-testid="hero-build-stage" initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4 }}
              className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-neutral-100">{STAGES[stage][1]}</motion.span>
          </AnimatePresence>
        </div>
        <button onClick={() => scrollToId("transformations")} data-testid="hero-scroll-cue" className="hidden items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.25em] text-neutral-400 md:flex">
          Scroll <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
        </button>
      </motion.div>
    </section>
  );
};
